import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Tag, Plus, X, Shield, FileCode, Info, AlertCircle, Building2, Languages, Type, FileText } from "lucide-react";

const languages = [
  { code: "de", label: "German" },
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
];

export const MetadataSection = ({ meta, setMeta, loading, errors = {} }) => {
  const [newKeyword, setNewKeyword] = useState("");
  const [fetchedKeywords, setFetchedKeywords] = useState([]);

  useEffect(() => {
    if (meta?.keywords && fetchedKeywords.length === 0) {
      setFetchedKeywords([...meta.keywords]);
    }
  }, [meta?.keywords, fetchedKeywords.length]);

  const isDataLoaded = meta && meta.title && !loading;
  const isInvalid = (val) => !val || (typeof val === 'string' && val.trim() === "");

  const addKeyword = () => {
    const trimmed = newKeyword.trim();
    if (trimmed && !meta.keywords.includes(trimmed)) {
      setMeta((m) => ({ ...m, keywords: [...m.keywords, trimmed] }));
    }
    setNewKeyword("");
  };

  const removeKeyword = (kw) => {
    if (fetchedKeywords.includes(kw)) return;
    setMeta((m) => ({ ...m, keywords: m.keywords.filter((k) => k !== kw) }));
  };

  // Label Component with Tooltip text
  const FieldLabel = ({ icon: Icon, label, required, tooltip }) => (
    <div className="flex items-center justify-between mb-3 px-1">
      <span className="flex items-center gap-2 text-sm font-bold text-gray-800 uppercase tracking-wider">
        <Icon size={16} className="text-gray-400" /> 
        {label} {required}
      </span>
      <div className="group relative">
        <button type="button" className="cursor-help">
          <Info size={16} className="text-green-300 hover:text-green-600 transition-colors" />
        </button>
        <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-64 p-3 bg-slate-900 text-white text-[11px] leading-relaxed rounded-xl shadow-2xl z-50 border border-slate-700 font-medium">
          {tooltip}
          <div className="absolute top-full right-2 border-8 border-transparent border-t-slate-900" />
        </div>
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      {isDataLoaded && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8 mt-10 pt-10 border-t-2 border-slate-50"
        >
          <div className="flex items-center gap-3 mb-6">
             <div/>
             <h2 className="text-lg font-semibold text-black uppercase tracking-widest">Dataset Specifications</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <FieldLabel 
                icon={FileCode} 
                label="OpenAPI Specification" 
                required 
                tooltip="The URL to the OpenAPI/Swagger JSON or YAML definition. This allows the system to map your API endpoints to the Knowledge Graph."
              />
              <div className="relative">
                <input
                  type="url"
                  value={meta.endpointDescription || ""}
                  onChange={(e) => setMeta({ ...meta, endpointDescription: e.target.value })}
                  className={`w-full p-5 rounded-2xl border-2 transition-all outline-none text-base font-medium
                    ${isInvalid(meta.endpointDescription) 
                      ? "border-red-200 bg-red-50 opacity-100" 
                      : "border-slate-100 bg-slate-50/50 opacity-60 focus:opacity-100 focus:bg-white focus:border-green-500 focus:shadow-lg focus:shadow-green-50"}`}
                />
                {isInvalid(meta.endpointDescription) && <AlertCircle size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-red-400" />}
              </div>
            </div>

            <div>
              <FieldLabel
                icon={Shield}
                label="License Type"
                required
                tooltip="The legal license under which this data is provided (e.g., Creative Commons, MIT, or a specific governmental open data license)."
              />
              <div className="relative">
                <input
                  type="text"
                  value={meta.license || ""}
                  onChange={(e) => setMeta({ ...meta, license: e.target.value })}
                  className={`w-full p-5 rounded-2xl border-2 transition-all outline-none text-base font-medium
                    ${isInvalid(meta.license)
                      ? "border-red-200 bg-red-50 opacity-100"
                      : "border-slate-100 bg-slate-50/50 opacity-60 focus:opacity-100 focus:bg-white focus:border-green-500 focus:shadow-lg focus:shadow-green-50"}`}
                />
                {isInvalid(meta.license) && <AlertCircle size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-red-400" />}
              </div>
            </div>
          </div>

          <div>
            <FieldLabel
              icon={FileText}
              label="License Attribution Text"
              tooltip="Optional human-readable attribution text for the license. Type any free-text attribution."
            />
            <textarea
              rows={2}
              value={meta.termsOfService || ""}
              onChange={(e) => setMeta({ ...meta, termsOfService: e.target.value })}
              placeholder="Type the license attribution text..."
              className="w-full p-5 rounded-2xl border-2 border-slate-100 bg-slate-50/50 transition-all outline-none text-base font-medium leading-relaxed focus:bg-white focus:border-green-500 focus:shadow-lg focus:shadow-green-50 resize-y"
            />
          </div>

          <div className="space-y-8">
            <div>
              <FieldLabel 
                icon={Type} 
                label="Dataset Title" 
                required 
                tooltip="A clear, concise name for the dataset. This is the primary name users will see in the catalog search results."
              />
              <div className="relative">
                <input
                  type="text"
                  value={meta.title || ""}
                  onChange={(e) => setMeta({ ...meta, title: e.target.value })}
                  className={`w-full p-5 rounded-2xl border-2 transition-all outline-none text-l font-bold
                    ${isInvalid(meta.title) 
                      ? "border-red-200 bg-red-50 opacity-100" 
                      : "border-slate-100 bg-slate-50/50 opacity-70 focus:opacity-100 focus:bg-white focus:border-green-500 focus:shadow-lg focus:shadow-green-50"}`}
                />
                {isInvalid(meta.title) && <AlertCircle size={24} className="absolute right-4 top-1/2 -translate-y-1/2 text-red-400" />}
              </div>
            </div>

            <div>
              <FieldLabel 
                icon={Info} 
                label="Full Description" 
                required 
                tooltip="A detailed explanation of what the dataset contains, its purpose, and any relevant context for potential users."
              />
              <div className="relative">
                <textarea
                  rows={4}
                  value={meta.description || ""}
                  onChange={(e) => setMeta({ ...meta, description: e.target.value })}
                  className={`w-full p-5 rounded-2xl border-2 transition-all outline-none text-base leading-relaxed font-medium
                    ${isInvalid(meta.description) 
                      ? "border-red-200 bg-red-50 opacity-100" 
                      : "border-slate-100 bg-slate-50/50 opacity-60 focus:opacity-100 focus:bg-white focus:border-green-500 focus:shadow-lg focus:shadow-green-50"}`}
                />
                {isInvalid(meta.description) && <AlertCircle size={20} className="absolute right-4 top-5 text-red-400" />}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <FieldLabel 
                icon={Building2} 
                label="Data Provider" 
                required 
                tooltip="The organization or entity responsible for publishing and maintaining this dataset (e.g., 'Bavarian State Office for Statistics')."
              />
              <div className="relative">
                <input
                  type="text"
                  value={meta.provider || ""}
                  onChange={(e) => setMeta({ ...meta, provider: e.target.value })}
                  className={`w-full p-5 rounded-2xl border-2 transition-all outline-none text-base font-medium
                    ${isInvalid(meta.provider) 
                      ? "border-red-200 bg-red-50 opacity-100" 
                      : "border-slate-100 bg-slate-50/50 opacity-60 focus:opacity-100 focus:bg-white focus:border-green-500"}`}
                />
                {isInvalid(meta.provider) && <AlertCircle size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-red-400" />}
              </div>
            </div>

            <div>
              <FieldLabel 
                icon={Languages} 
                label="Content Language" 
                required 
                tooltip="The primary language used within the dataset content and metadata."
              />
              <select
                value={meta.language || ""}
                onChange={(e) => setMeta({ ...meta, language: e.target.value })}
                className={`w-full p-5 border-2 rounded-2xl focus:bg-white focus:border-green-500 transition-all outline-none cursor-pointer text-base font-bold text-slate-700 appearance-none
                  ${!meta.language
                    ? "border-red-200 bg-red-50 opacity-100"
                    : "border-slate-100 bg-slate-50/50 opacity-60 focus:opacity-100"}`}
              >
                <option value="">Select a language...</option>
                {languages.map(({ code, label }) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-5">
            <FieldLabel 
              icon={Tag} 
              label="Keywords" 
              required 
              tooltip="Tags used to categorize the dataset. These help improve the discoverability of your data in the catalog."
            />
            <div className="flex gap-3">
              <input
                type="text"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addKeyword())}
                placeholder="Type a keyword to add to fetched keywords..."
                className="flex-1 p-5 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:border-blue-400 focus:bg-white outline-none transition-all text-base"
              />
              <button
                type="button"
                onClick={addKeyword}
                className="px-10 bg-green-50 text-green-700  rounded-2xl font-black text-base hover:bg-green-600 hover:text-white active:scale-95 transition-all flex items-center gap-2 shadow-lg"
              >
                <Plus size={20} /> Add
              </button>
            </div>

            <div className={`flex flex-wrap gap-3 p-6 rounded-[2rem] border-2 border-dashed transition-all min-h-[120px] items-center
              ${meta.keywords?.length === 0 ? "border-red-200 bg-red-50" : "border-slate-200 bg-slate-50/30"}`}>
              {(meta.keywords || []).map((kw) => {
                const isFetched = fetchedKeywords.includes(kw);
                return (
                  <motion.span
                    key={kw}
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-black border transition-all shadow-sm
                      ${isFetched ? "bg-white text-slate-400 border-slate-200" : "bg-green-50 text-green-700 border-green-200"}`}
                  >
                    {kw}
                    {!isFetched && (
                      <button onClick={() => removeKeyword(kw)} className="hover:text-red-500 transition-colors">
                        <X size={16} />
                      </button>
                    )}
                  </motion.span>
                );
              })}
              {meta.keywords?.length === 0 && (
                <span className="text-red-500 text-sm font-bold flex items-center gap-2 mx-auto uppercase tracking-wider bg-white px-4 py-2 rounded-full border border-red-100">
                  <AlertCircle size={18} /> At least one keyword is required
                </span>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
